import React, { useState, useEffect } from "react";
import { AdminContact } from "../../types/admin";
import { contactsApi } from "../../services/api";
import { useToast } from "./Toast";

const emptyContact: Omit<AdminContact, "id"> = {
  platform: "",
  url: "",
  iconClass: "",
  label: "",
};

const platformPresets: Record<string, { iconClass: string; placeholder: string }> = {
  LinkedIn: { iconClass: "fa-brands fa-linkedin-in", placeholder: "https://linkedin.com/in/..." },
  GitHub: { iconClass: "fa-brands fa-github", placeholder: "https://github.com/..." },
  WhatsApp: { iconClass: "fa-brands fa-whatsapp", placeholder: "https://wa.me/..." },
  Instagram: { iconClass: "fa-brands fa-instagram", placeholder: "https://instagram.com/..." },
  Twitter: { iconClass: "fa-brands fa-x-twitter", placeholder: "https://x.com/..." },
  Email: { iconClass: "fa-solid fa-envelope", placeholder: "mailto:you@example.com" },
  Website: { iconClass: "fa-solid fa-globe", placeholder: "https://..." },
};

const ContactsPage: React.FC = () => {
  const { showToast } = useToast();
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<Omit<AdminContact, "id">>(emptyContact);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await contactsApi.getAll();
      if (response.success && response.data) {
        setContacts(response.data);
      } else {
        showToast(response.message || "Failed to load contacts", "error");
      }
    } catch {
      showToast("Failed to load contacts", "error");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyContact);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (contact: AdminContact) => {
    setEditingId(contact.id);
    setForm({
      platform: contact.platform,
      url: contact.url,
      iconClass: contact.iconClass,
      label: contact.label,
    });
    setErrors({});
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.platform.trim()) newErrors.platform = "Platform is required";
    if (!form.url.trim()) newErrors.url = "URL is required";
    if (!form.iconClass.trim()) newErrors.iconClass = "Icon class is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        const response = await contactsApi.update(editingId, form);
        if (response.success) {
          showToast(response.message || "Contact updated successfully");
          setModalOpen(false);
          loadContacts();
        } else {
          showToast(response.message || "Failed to update", "error");
        }
      } else {
        const response = await contactsApi.create(form);
        if (response.success) {
          showToast(response.message || "Contact created successfully");
          setModalOpen(false);
          loadContacts();
        } else {
          showToast(response.message || "Failed to create", "error");
        }
      }
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;

    try {
      const response = await contactsApi.delete(id);
      if (response.success) {
        showToast(response.message || "Contact deleted");
        loadContacts();
      } else {
        showToast(response.message || "Failed to delete", "error");
      }
    } catch {
      showToast("An error occurred", "error");
    }
  };

  const handlePlatformPreset = (platform: string) => {
    const preset = platformPresets[platform];
    if (preset) {
      setForm((prev) => ({
        ...prev,
        platform,
        iconClass: preset.iconClass,
        label: platform,
      }));
    }
  };

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Contacts</h1>
        <p className="admin-page-subtitle">
          Manage your social links and contact information
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">
            Social Links ({contacts.length})
          </span>
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={openCreate}>
            <i className="fa-solid fa-plus" />
            <span>Add Contact</span>
          </button>
        </div>

        {contacts.length === 0 ? (
          <div className="admin-empty-state">
            <i className="fa-solid fa-address-book" />
            <p>No contacts yet</p>
            <button className="admin-btn admin-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" />
              <span>Add Your First Contact</span>
            </button>
          </div>
        ) : (
          <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Platform</th>
                <th>URL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td>
                    <i
                      className={contact.iconClass}
                      style={{ fontSize: "2rem", color: "var(--color-secondary)" }}
                    />
                  </td>
                  <td style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                    {contact.platform}
                  </td>
                  <td>
                    <a
                      href={contact.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--color-secondary)", fontSize: "1.3rem" }}
                    >
                      {contact.url.length > 40
                        ? contact.url.substring(0, 40) + "..."
                        : contact.url}
                    </a>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => openEdit(contact)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        onClick={() => handleDelete(contact.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="admin-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {editingId ? "Edit Contact" : "New Contact"}
              </h2>
              <button className="admin-modal-close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="admin-modal-body">
              {/* Quick Presets */}
              <div className="admin-form-group">
                <label className="admin-form-label">Quick Presets</label>
                <div className="admin-btn-group">
                  {Object.keys(platformPresets).map((key) => (
                    <button
                      key={key}
                      className={`admin-btn admin-btn-sm ${
                        form.platform === key ? "admin-btn-primary" : "admin-btn-secondary"
                      }`}
                      onClick={() => handlePlatformPreset(key)}
                    >
                      <i className={platformPresets[key].iconClass} />
                      <span>{key}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Platform</label>
                  <input
                    type="text"
                    className={`admin-form-input${errors.platform ? " admin-input-error" : ""}`}
                    placeholder="e.g. LinkedIn"
                    value={form.platform}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, platform: e.target.value }));
                      setErrors((prev) => ({ ...prev, platform: "" }));
                    }}
                  />
                  {errors.platform && <span className="admin-field-error">{errors.platform}</span>}
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Label</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    placeholder="e.g. My LinkedIn"
                    value={form.label}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, label: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">URL</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.url ? " admin-input-error" : ""}`}
                  placeholder={
                    platformPresets[form.platform]?.placeholder || "https://..."
                  }
                  value={form.url}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, url: e.target.value }));
                    setErrors((prev) => ({ ...prev, url: "" }));
                  }}
                />
                {errors.url && <span className="admin-field-error">{errors.url}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Icon Class</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.iconClass ? " admin-input-error" : ""}`}
                  placeholder="e.g. fa-brands fa-linkedin-in"
                  value={form.iconClass}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, iconClass: e.target.value }));
                    setErrors((prev) => ({ ...prev, iconClass: "" }));
                  }}
                />
                <span className="admin-form-hint">
                  Font Awesome class name — auto-filled from presets
                </span>
                {errors.iconClass && <span className="admin-field-error">{errors.iconClass}</span>}
              </div>

              {/* Preview */}
              {form.iconClass && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Preview</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1.5rem",
                      background: "var(--color-surface-light)",
                      borderRadius: "0.8rem",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <i
                      className={form.iconClass}
                      style={{ fontSize: "2.5rem", color: "var(--color-secondary)" }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-text-primary)", fontSize: "1.4rem" }}>
                        {form.platform || "Platform"}
                      </div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "1.2rem" }}>
                        {form.url || "URL"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
                <span>{saving ? "Saving..." : editingId ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
